import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
    ArrowLeft, MapPin, Binoculars, Bird, Fish, PawPrint, Bug, Info
} from '@phosphor-icons/react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const TAXON_FILTERS = [
    { key: 'all', label: 'All', icon: Binoculars },
    { key: 'Mammalia', label: 'Mammals', icon: PawPrint },
    { key: 'Aves', label: 'Birds', icon: Bird },
    { key: 'Reptilia', label: 'Reptiles', icon: Bug },
    { key: 'Amphibia', label: 'Amphibians', icon: Bug },
    { key: 'Actinopterygii', label: 'Fish', icon: Fish },
];

function createColoredIcon(color) {
    return L.divIcon({
        className: '',
        html: `<div style="width:24px;height:24px;border-radius:50%;background:${color};border:3px solid #0f172a;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });
}

function SightingCard({ sighting, theme, index }) {
    const hasOurAnimal = !!sighting.our_animal_id;
    const CardWrapper = hasOurAnimal ? Link : 'div';
    const linkProps = hasOurAnimal ? { to: `/animals/${sighting.our_animal_id}` } : {};

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * Math.min(index, 12) }}
        >
            <CardWrapper
                {...linkProps}
                className="group block border-4 border-[#0f172a] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[5px_5px_0px_#0f172a] transition-all"
                style={{ backgroundColor: theme.card_bg }}
                data-testid={`sighting-card-${index}`}
            >
                {/* Photo */}
                <div className="h-36 sm:h-40 relative overflow-hidden bg-[#e2e8f0]">
                    {sighting.photo_url ? (
                        <>
                            <img
                                src={sighting.photo_url}
                                alt={sighting.common_name}
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="w-full h-full items-center justify-center absolute inset-0 hidden" style={{ backgroundColor: theme.bg }}>
                                <PawPrint weight="duotone" size={48} style={{ color: theme.accent, opacity: 0.4 }} />
                            </div>
                            {/* Photo credit (visible on hover) */}
                            {sighting.photo_url && (
                                <span
                                    role="link"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const url = sighting.taxon_id ? `https://www.inaturalist.org/taxa/${sighting.taxon_id}` : 'https://www.inaturalist.org';
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                    }}
                                    className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-[#0f172a]/85 text-[10px] leading-snug text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-start gap-1 backdrop-blur-sm cursor-pointer hover:underline"
                                    data-testid={`photo-credit-${index}`}
                                    title="View on iNaturalist"
                                >
                                    <Info weight="bold" size={11} className="mt-px shrink-0" />
                                    <span className="line-clamp-2">
                                        {sighting.photo_attribution || 'Photo via iNaturalist'} · iNaturalist
                                    </span>
                                </span>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
                            <PawPrint weight="duotone" size={48} style={{ color: theme.accent, opacity: 0.4 }} />
                        </div>
                    )}
                    {/* Observation count badge */}
                    <div
                        className="absolute top-2 right-2 px-2.5 py-1 rounded-lg border-2 border-[#0f172a] text-xs font-bold text-white"
                        style={{ backgroundColor: theme.accent }}
                    >
                        {sighting.observation_count.toLocaleString()} sightings
                    </div>
                    {/* Taxon badge */}
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-[#0f172a]/70 text-xs font-semibold text-white backdrop-blur-sm">
                        {sighting.iconic_taxon || 'Unknown'}
                    </div>
                </div>
                {/* Info */}
                <div className="p-3.5">
                    <h3 className="font-fredoka font-bold text-base leading-tight" style={{ color: theme.text }}>
                        {sighting.common_name}
                    </h3>
                    <p className="text-xs italic mt-0.5 opacity-60" style={{ color: theme.text }}>
                        {sighting.scientific_name}
                    </p>
                    {hasOurAnimal && (
                        <div
                            className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border-2 border-[#0f172a] text-xs font-bold"
                            style={{ backgroundColor: theme.bg, color: theme.accent }}
                        >
                            <PawPrint weight="bold" size={12} /> In Our Library
                        </div>
                    )}
                </div>
            </CardWrapper>
        </motion.div>
    );
}

export default function LocationDetailPage() {
    const { habitatId, locationIndex } = useParams();
    const { api } = useAuth();
    const [habitat, setHabitat] = useState(null);
    const [sightings, setSightings] = useState([]);
    const [totalSpecies, setTotalSpecies] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageLoading, setPageLoading] = useState(false);
    const locIdx = parseInt(locationIndex, 10);

    useEffect(() => {
        const load = async () => {
            try {
                // Load habitat details and sightings in parallel
                const [habitatRes, sightingsRes] = await Promise.all([
                    api().get(`/habitats/${habitatId}`),
                    api().get(`/habitats/${habitatId}/locations/${locIdx}/sightings`)
                ]);
                setHabitat(habitatRes.data);
                setSightings(sightingsRes.data.sightings || []);
                setTotalSpecies(sightingsRes.data.total_species || 0);
                setTotalPages(sightingsRes.data.total_pages || 1);
                setPage(1);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load location data');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [habitatId, locIdx]);

    const goToPage = async (p) => {
        if (p < 1 || p > totalPages || p === page || pageLoading) return;
        setPageLoading(true);
        try {
            const res = await api().get(`/habitats/${habitatId}/locations/${locIdx}/sightings`, { params: { page: p } });
            setSightings(res.data.sightings || []);
            setPage(p);
            setActiveFilter('all');
            document.querySelector('[data-testid=sightings-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) { /* keep current page */ } finally {
            setPageLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-14 h-14 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="font-fredoka font-bold text-[#0f172a]">Loading wildlife sightings...</p>
                </div>
            </div>
        );
    }

    if (error || !habitat) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center p-4">
                <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-8 text-center max-w-md">
                    <Binoculars weight="duotone" size={48} className="text-[#ef4444] mx-auto mb-3" />
                    <p className="font-fredoka text-xl font-bold text-[#0f172a] mb-2">Could Not Load Sightings</p>
                    <p className="text-[#475569] mb-4">{error}</p>
                    <Link
                        to={`/habitats/${habitatId}`}
                        className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-6 py-3 inline-block hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                        data-testid="back-to-habitat-error"
                    >
                        Back to Habitat
                    </Link>
                </div>
            </div>
        );
    }

    const theme = habitat.theme || { bg: '#f0fdf4', accent: '#22c55e', text: '#0f172a', card_bg: '#fff' };
    const locations = habitat.locations || [];
    const location = locations[locIdx];
    const icon = createColoredIcon(theme.accent);

    const filtered = activeFilter === 'all'
        ? sightings
        : sightings.filter(s => s.iconic_taxon === activeFilter);

    // Count per taxon for filter badges
    const taxonCounts = {};
    for (const s of sightings) {
        const t = s.iconic_taxon || 'Unknown';
        taxonCounts[t] = (taxonCounts[t] || 0) + 1;
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
            {/* Nav */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link
                            to={`/habitats/${habitatId}`}
                            className="flex items-center gap-2"
                            data-testid="back-to-habitat"
                        >
                            <div
                                className="w-9 h-9 rounded-full border-3 border-[#0f172a] flex items-center justify-center"
                                style={{ backgroundColor: theme.accent }}
                            >
                                <span className="text-lg">{habitat.emoji}</span>
                            </div>
                            <span className="font-fredoka text-base font-bold text-[#0f172a] hidden sm:block truncate max-w-[200px]">
                                {habitat.name}
                            </span>
                        </Link>
                        <Link
                            to={`/habitats/${habitatId}`}
                            className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                            data-testid="back-to-habitat-nav"
                        >
                            <ArrowLeft weight="bold" size={16} /> Back
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-3xl overflow-hidden mb-8"
                    style={{ backgroundColor: theme.card_bg }}
                    data-testid="location-hero"
                >
                    <div className="grid md:grid-cols-2">
                        {/* Map */}
                        <div className="h-56 md:h-72">
                            {location && (
                                <MapContainer
                                    center={[location.lat, location.lng]}
                                    zoom={10}
                                    scrollWheelZoom={false}
                                    dragging={false}
                                    zoomControl={false}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <Marker position={[location.lat, location.lng]} icon={icon} />
                                </MapContainer>
                            )}
                        </div>
                        {/* Info */}
                        <div className="p-6 md:p-8 flex flex-col justify-center">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin weight="bold" size={20} style={{ color: theme.accent }} />
                                <span className="text-sm font-bold opacity-60" style={{ color: theme.text }}>
                                    {location?.state} · Near {location?.nearest_city}
                                </span>
                            </div>
                            <h1 className="font-fredoka text-2xl sm:text-3xl font-bold mb-3" style={{ color: theme.text }}>
                                {location?.name || 'Location'}
                            </h1>
                            <p className="text-sm leading-relaxed mb-4" style={{ color: `${theme.text}99` }}>
                                {location?.description}
                            </p>
                            {/* Stats */}
                            <div className="flex flex-wrap gap-3">
                                <div
                                    className="px-4 py-2 rounded-xl border-3 border-[#0f172a] font-fredoka font-bold text-sm text-white"
                                    style={{ backgroundColor: theme.accent }}
                                    data-testid="total-species-stat"
                                >
                                    <Binoculars weight="bold" size={16} className="inline mr-1.5 -mt-0.5" />
                                    {totalSpecies.toLocaleString()} species nearby
                                </div>
                                <div
                                    className="px-4 py-2 rounded-xl border-3 border-[#0f172a] font-fredoka font-bold text-sm"
                                    style={{ backgroundColor: theme.bg, color: theme.text }}
                                    data-testid="sightings-count-stat"
                                >
                                    {sightings.length} shown
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Filter Tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-wrap gap-2 mb-6"
                    data-testid="taxon-filters"
                >
                    {TAXON_FILTERS.map(f => {
                        const count = f.key === 'all' ? sightings.length : (taxonCounts[f.key] || 0);
                        if (f.key !== 'all' && count === 0) return null;
                        const isActive = activeFilter === f.key;
                        const Icon = f.icon;
                        return (
                            <button
                                key={f.key}
                                onClick={() => setActiveFilter(f.key)}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border-3 border-[#0f172a] font-fredoka font-bold text-sm transition-all ${
                                    isActive
                                        ? 'text-white shadow-[3px_3px_0px_#0f172a] -translate-y-0.5'
                                        : 'bg-white hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_#0f172a]'
                                }`}
                                style={isActive ? { backgroundColor: theme.accent, color: '#fff' } : { color: theme.text }}
                                data-testid={`filter-${f.key}`}
                            >
                                <Icon weight="bold" size={16} />
                                {f.label}
                                <span className={`ml-0.5 text-xs ${isActive ? 'opacity-80' : 'opacity-50'}`}>
                                    ({count})
                                </span>
                            </button>
                        );
                    })}
                </motion.div>

                {/* Sightings Grid */}
                {filtered.length > 0 ? (
                    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 transition-opacity ${pageLoading ? 'opacity-40' : ''}`} data-testid="sightings-grid">
                        {filtered.map((s, i) => (
                            <SightingCard key={s.taxon_id || i} sighting={s} theme={theme} index={i} />
                        ))}
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-2xl p-10 text-center"
                        style={{ backgroundColor: theme.card_bg }}
                        data-testid="no-sightings"
                    >
                        <Binoculars weight="duotone" size={48} style={{ color: theme.accent }} className="mx-auto mb-3 opacity-50" />
                        <p className="font-fredoka font-bold text-lg" style={{ color: theme.text }}>
                            No {activeFilter !== 'all' ? TAXON_FILTERS.find(f => f.key === activeFilter)?.label : ''} sightings found
                        </p>
                        <p className="text-sm mt-1 opacity-60" style={{ color: theme.text }}>
                            Try selecting a different filter above
                        </p>
                    </motion.div>
                )}

                {/* Pager */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-6" data-testid="sightings-pager">
                        <button
                            onClick={() => goToPage(page - 1)}
                            disabled={page <= 1 || pageLoading}
                            className="px-4 py-2 rounded-xl border-4 border-[#0f172a] bg-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform"
                            data-testid="sightings-prev"
                        >
                            ‹ Prev
                        </button>
                        <span className="font-fredoka font-bold text-[#0f172a] text-sm" data-testid="sightings-page-indicator">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => goToPage(page + 1)}
                            disabled={page >= totalPages || pageLoading}
                            className="px-4 py-2 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform"
                            data-testid="sightings-next"
                        >
                            Next ›
                        </button>
                    </div>
                )}

                {/* iNaturalist attribution */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-center text-xs mt-8 opacity-40 font-semibold"
                    style={{ color: theme.text }}
                    data-testid="inaturalist-attribution"
                >
                    Wildlife data powered by iNaturalist &middot; Research-grade observations within 20 km
                </motion.p>
            </div>
        </div>
    );
}
